import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, FileDown, Maximize2, Search, X } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { Lightbox } from "@/components/Lightbox";
import { Slideshow } from "@/components/Slideshow";
import { PlotMap } from "@/components/PlotMap";
import { PaymentOptions } from "@/components/PaymentOptions";
import { VideoGallery } from "@/components/VideoGallery";
import { LoopVideo } from "@/components/LoopVideo";
import { API, currency, idr, plotPrice, plotPriceLabel } from "@/lib/api";

const TYPES = [
  { id: "all", label: "All" },
  { id: "resort", label: "Resorts" },
  { id: "villa", label: "Villas" },
  { id: "land", label: "Land" },
  { id: "estate", label: "Estates" },
];

const PRICES = [
  { id: "any", label: "Any price", min: null, max: null },
  { id: "u500", label: "Under $500K", min: 0, max: 500000 },
  { id: "500-1", label: "$500K – $1M", min: 500000, max: 1000000 },
  { id: "1-2", label: "$1M – $2M", min: 1000000, max: 2000000 },
  { id: "2p", label: "Above $2M", min: 2000000, max: null },
];

const SORTS = [
  { id: "featured", label: "Featured first" },
  { id: "price_asc", label: "Price · low to high" },
  { id: "price_desc", label: "Price · high to low" },
  { id: "yield", label: "Top yield first" },
];

const statusChip = (s) =>
  s === "available"
    ? "bg-gold/95 text-ink"
    : s === "reserved"
    ? "bg-cream/90 text-pine"
    : "bg-ink/80 text-cream";

const sorters = {
  featured: (a, b) => Number(b.featured) - Number(a.featured) || a.price_usd - b.price_usd,
  price_asc: (a, b) => a.price_usd - b.price_usd,
  price_desc: (a, b) => b.price_usd - a.price_usd,
  yield: (a, b) => (b.yield_pct || 0) - (a.yield_pct || 0),
};

function Card({ l, onOpen }) {
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="group cursor-pointer"
      onClick={() => onOpen(l)}
      data-testid={`listing-card-${l.id}`}
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-sand">
        {l.videos?.length ? (
          <LoopVideo
            src={l.videos[0].src}
            poster={l.images[0]}
            testId={`listing-card-video-${l.id}`}
            className="transition-transform duration-[1400ms] ease-out group-hover:scale-[1.07]"
          />
        ) : (
        <Slideshow
          images={l.images}
          alt={l.title}
          dots={false}
          className="h-full w-full transition-transform duration-[1400ms] ease-out group-hover:scale-[1.07]"
          testId={`listing-card-slideshow-${l.id}`}
        />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent opacity-80" />
        <span
          className={`absolute left-4 top-4 rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${statusChip(l.status)}`}
          data-testid={`listing-status-${l.id}`}
        >
          {l.status}
        </span>
        <span className="absolute right-4 top-4 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/90">
          {l.type}
        </span>
        <div className="absolute inset-x-0 bottom-0 p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-cream/75">
            {l.location}
          </p>
          <div className="mt-1.5 flex flex-wrap items-end justify-between gap-2">
            <h3 className="font-display text-2xl text-cream">{l.title}</h3>
            <span className="font-mono text-sm text-gold">
              {l.price_idr_per_are
                ? `${idr(l.price_idr_per_are)} / are`
                : l.price_per_sqm
                ? `from ${currency(l.price_per_sqm)} / m²`
                : `${l.units?.length ? "from " : ""}${currency(l.price_usd)}`}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between border-b border-pine/10 py-3 font-mono text-[11px] uppercase tracking-[0.14em] text-moss">
        <span>
          {l.units?.length
            ? `${l.units[0].land_ara} ara land`
            : `${l.land_sqm.toLocaleString()} m² ${l.development ? "plotted" : "land"}`}
        </span>
        {l.bedrooms ? (
          <span>{l.bedrooms} bedrooms</span>
        ) : l.units?.length ? (
          <span>
            {l.units.reduce((s, u) => s + (u.count || 1), 0)} villas
          </span>
        ) : l.development ? (
          <span>{(l.development.total_sqm / 10000).toFixed(2)} ha masterplan</span>
        ) : (
          <span>plot</span>
        )}
        <span className="text-terracotta" data-testid={`listing-yield-${l.id}`}>
          {l.yield_pct
            ? `${l.yield_pct}% p.a. yield`
            : `${l.appreciation_pct}% p.a. land`}
        </span>
      </div>
    </motion.article>
  );
}

function VillaGallery({ images, name, slug }) {
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-6" data-testid={`villa-gallery-${slug}`}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Open ${name} gallery`}
        data-testid={`villa-gallery-main-${slug}`}
        className="group relative block w-full cursor-zoom-in overflow-hidden rounded-xl"
      >
        <img
          src={images[active]}
          alt={name}
          className="aspect-[16/10] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
        <span className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-ink/70 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-sand opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          <Maximize2 className="h-3 w-3" /> View {images.length} photos
        </span>
      </button>
      <Lightbox
        images={images}
        name={name}
        index={open ? active : null}
        onChange={setActive}
        onClose={() => setOpen(false)}
      />
      <div className="mt-2 grid grid-cols-5 gap-2">
        {images.map((im, i) => (
          <button
            key={im}
            type="button"
            onClick={() => setActive(i)}
            data-testid={`villa-gallery-thumb-${slug}-${i}`}
            className={`aspect-square overflow-hidden rounded-lg transition-opacity duration-200 ${
              i === active ? "ring-2 ring-terracotta" : "opacity-70 hover:opacity-100"
            }`}
          >
            <img src={im} alt={`${name} ${i + 1}`} className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}

function DevelopmentSection({ l, plot, onSelectPlot }) {
  const d = l.development;
  const allPlots = (d.plans || []).flatMap((p) => p.plots);
  const cheapest = allPlots
    .filter((p) => p.status === "available" && !p.price_on_request)
    .sort((a, b) => a.sqm - b.sqm)[0];
  const payPlot = plot ? (plot.price_on_request ? null : plot) : cheapest;
  const soldPct = Math.round((d.sold_sqm / d.total_sqm) * 100);
  const phasePct = Math.round((d.phase_sqm / d.total_sqm) * 100);
  return (
    <div className="border-t border-pine/10 px-6 pb-10 pt-8 md:px-10" data-testid="listing-development">
      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-pine">The development</p>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-moss">
        Released one portion at a time. {(d.sold_sqm / 10000).toFixed(2)} ha of the{" "}
        {(d.total_sqm / 10000).toFixed(2)} ha masterplan is already sold; the current{" "}
        {d.phase_sqm.toLocaleString()} m² section is plotted and selling now.
      </p>
      <div className="mt-5 h-3 w-full overflow-hidden rounded-full bg-sand" data-testid="development-progress">
        <div className="flex h-full">
          <div className="h-full bg-pine" style={{ width: `${soldPct}%` }} title="Sold" />
          <div className="h-full bg-terracotta" style={{ width: `${phasePct}%` }} title="Now selling" />
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 font-mono text-[10px] uppercase tracking-[0.16em] text-moss">
        <span><i className="mr-1.5 inline-block h-2 w-2 rounded-full bg-pine" />Sold · {soldPct}%</span>
        <span><i className="mr-1.5 inline-block h-2 w-2 rounded-full bg-terracotta" />Now selling · {phasePct}%</span>
        <span><i className="mr-1.5 inline-block h-2 w-2 rounded-full bg-sand ring-1 ring-pine/20" />Future phases</span>
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-pine/10 bg-sand/50 p-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-moss">Release plan</p>
          <ul className="mt-4 space-y-3">
            {d.phases.map((p, i) => (
              <li key={p} className="flex items-start gap-3 text-sm text-pine" data-testid={`development-phase-${i}`}>
                <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${i === 0 ? "bg-pine" : i === 1 ? "bg-terracotta" : "bg-sand ring-1 ring-pine/30"}`} />
                {p}
              </li>
            ))}
          </ul>
          {d.villas_contracted && (
            <p className="mt-6 font-display text-2xl text-pine" data-testid="development-villas">
              {d.villas_contracted} villas <em className="text-terracotta">already contracted</em>
            </p>
          )}
          <p className="mt-2 text-sm leading-relaxed text-moss">
            Construction inside the residence lifts the value of every neighbouring plot — early-phase buyers ride that curve.
          </p>
        </div>
        <PlotCalculator l={l} />
      </div>
      {d.plans?.length > 0 && (
        <div className="mt-10">
          <PlotMap development={d} listing={l} selected={plot} onSelect={onSelectPlot} />
        </div>
      )}
      {payPlot && (
        <div className="mt-8">
          <PaymentOptions
            price={plotPrice(l, payPlot.sqm, payPlot.price_idr_per_are || undefined).usd}
            priceNote={`Plot ${payPlot.number} · ${payPlot.sqm.toLocaleString()} m² · ${plotPriceLabel(l, payPlot.sqm, payPlot.price_idr_per_are || undefined)}`}
            incomeRate={l.appreciation_pct}
            incomeLabel="land appreciation"
            slug="mawun"
            financing={false}
          />
        </div>
      )}
      {plot?.price_on_request && (
        <p className="mt-6 rounded-xl border border-pine/10 bg-sand/50 p-5 text-sm text-moss" data-testid="plot-price-on-request">
          Plot {plot.number} sits outside the current release — pricing and payment terms are shared on request. Send the form below and we'll come back with numbers.
        </p>
      )}
    </div>
  );
}

function PlotCalculator({ l }) {
  const [ara, setAra] = useState(5);
  const [years, setYears] = useState(5);
  const { idr: priceIdr, usd: price } = plotPrice(l, ara * 100);
  const rate = l.appreciation_pct || 0;
  const future = Math.round(price * Math.pow(1 + rate / 100, years));
  return (
    <div className="rounded-xl border border-pine/10 bg-white p-6" data-testid="plot-calculator">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-moss">Your plot · price calculator</p>
      <div className="mt-4 flex items-center justify-between">
        <p className="text-sm text-pine">Plot size</p>
        <p className="font-mono text-xs text-terracotta" data-testid="plot-calc-size">
          {ara} ara · {(ara * 100).toLocaleString()} m²
        </p>
      </div>
      <input
        type="range" min={2} max={20} step={0.5} value={ara}
        onChange={(e) => setAra(Number(e.target.value))}
        data-testid="plot-calc-size-slider" aria-label="Plot size"
        className="range-light mt-2" style={{ "--fill": `${((ara - 2) / 18) * 100}%` }}
      />
      <div className="mt-4 flex items-center justify-between">
        <p className="text-sm text-pine">Holding period</p>
        <p className="font-mono text-xs text-terracotta" data-testid="plot-calc-years">{years} years</p>
      </div>
      <input
        type="range" min={1} max={10} step={1} value={years}
        onChange={(e) => setYears(Number(e.target.value))}
        data-testid="plot-calc-years-slider" aria-label="Holding period"
        className="range-light mt-2" style={{ "--fill": `${((years - 1) / 9) * 100}%` }}
      />
      <div className="mt-5 grid grid-cols-2 gap-3 text-center">
        <div className="rounded-lg bg-sand/70 p-3">
          <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-sage">Plot price today</p>
          <p className="mt-1 font-display text-base md:text-lg" data-testid="plot-calc-price">{idr(priceIdr)}</p>
          <p className="font-mono text-[10px] text-moss">≈ {currency(price)}</p>
        </div>
        <div className="rounded-lg bg-pine p-3">
          <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-cream/60">Value · {years}y @ {rate}% p.a.</p>
          <p className="mt-1 font-display text-base md:text-lg text-gold" data-testid="plot-calc-future">{currency(future)}</p>
        </div>
      </div>
      <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.14em] text-sage">
        {idr(l.price_idr_per_are)} / are · market appreciation for {l.region} — illustrative, not a guarantee
      </p>
    </div>
  );
}

function VillaRoi({ unit }) {
  const rate = 13;
  const [years, setYears] = useState(5);
  const yearly = Math.round(unit.price_usd * (rate / 100));
  const cumulative = yearly * years;
  const total = unit.price_usd + cumulative;
  const slug = unit.name.toLowerCase().replace(/\s+/g, "-");

  return (
    <div className="rounded-xl border border-pine/10 bg-white p-5" data-testid={`villa-roi-${slug}`}>
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-moss">
          Return on investment · 13% p.a.
        </p>
        <p className="font-mono text-xs text-terracotta" data-testid={`villa-roi-years-${slug}`}>
          over {years} years
        </p>
      </div>
      <input
        type="range"
        min={1}
        max={10}
        step={1}
        value={years}
        onChange={(e) => setYears(Number(e.target.value))}
        data-testid={`villa-roi-slider-${slug}`}
        aria-label={`Holding period — ${unit.name}`}
        className="range-light mt-4"
        style={{ "--fill": `${((years - 1) / 9) * 100}%` }}
      />
      <div className="mt-4 grid grid-cols-3 gap-3 text-center">
        <div className="rounded-lg bg-sand/70 p-3">
          <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-sage">Per year</p>
          <p className="mt-1 font-display text-sm md:text-base" data-testid={`villa-roi-yearly-${slug}`}>
            {currency(yearly)}
          </p>
        </div>
        <div className="rounded-lg bg-sand/70 p-3">
          <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-sage">
            Income · {years}y
          </p>
          <p className="mt-1 font-display text-sm md:text-base" data-testid={`villa-roi-cumulative-${slug}`}>
            {currency(cumulative)}
          </p>
        </div>
        <div className="rounded-lg bg-pine p-3">
          <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-cream/60">
            Asset + income
          </p>
          <p className="mt-1 font-display text-sm md:text-base text-gold" data-testid={`villa-roi-total-${slug}`}>
            {currency(total)}
          </p>
        </div>
      </div>
      <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.14em] text-sage">
        Simple return on the purchase price — illustrative, not a guarantee
      </p>
    </div>
  );
}

function Modal({ l, onClose }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [sent, setSent] = useState(false);
  const [plot, setPlot] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    const plotNote = plot
      ? ` · Plot ${plot.number} (${plot.sqm.toLocaleString()} m², ${plot.price_on_request ? "price on request" : plotPriceLabel(l, plot.sqm, plot.price_idr_per_are || undefined)})`
      : "";
    try {
      await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          property_title: l.title,
          message: `Request for details: ${l.title}${plotNote}`,
        }),
      });
      setSent(true);
      toast.success("Request sent — details on their way.");
    } catch {
      toast.error("Could not send. Please try again.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] flex items-end justify-center bg-ink/70 backdrop-blur-sm md:items-center md:p-8"
      onClick={onClose}
      data-testid="listing-modal-backdrop"
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[92vh] w-full max-w-5xl overflow-y-auto bg-cream"
        data-lenis-prevent
        style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
        data-testid="listing-modal"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="listing-modal-close"
          className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-ink/60 text-cream backdrop-blur transition-colors hover:bg-ink"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="grid md:grid-cols-2">
          <div className="flex flex-col gap-2 p-3 md:p-4">
            <Slideshow
              images={l.images}
              alt={l.title}
              className="aspect-[4/3] w-full rounded-t-[5rem]"
              testId="listing-modal-slideshow"
            />
            <div className="grid grid-cols-4 gap-2">
              {l.images.slice(1, 5).map((im, i) => (
                <img
                  key={i}
                  src={im}
                  alt=""
                  className="aspect-square w-full rounded object-cover"
                />
              ))}
            </div>
          </div>

          <div className="p-6 md:p-10">
            <Eyebrow>{l.location}</Eyebrow>
            <h2 className="mt-3 font-display text-3xl md:text-4xl">{l.title}</h2>
            <p className="mt-4 font-mono text-2xl text-terracotta" data-testid="listing-modal-price">
              {l.price_idr_per_are
                ? `${idr(l.price_idr_per_are)} / are`
                : l.price_per_sqm
                ? `from ${currency(l.price_per_sqm)} / m²`
                : currency(l.price_usd)}
            </p>
            {l.price_idr_per_are && (
              <p className="mt-1 font-mono text-xs uppercase tracking-[0.14em] text-moss" data-testid="listing-modal-price-note">
                ≈ {currency(l.price_idr_per_are / l.fx_idr_per_usd)} / are · {currency(l.price_idr_per_are / l.fx_idr_per_usd / 100)} / m² · masterplan from {currency(l.price_per_sqm)} / m²
              </p>
            )}

            <div className="mt-6 grid grid-cols-2 gap-4 border-y border-pine/10 py-5 font-mono text-[11px] uppercase tracking-[0.12em] text-moss">
              <div>
                <p className="text-sage">{l.development ? "Now selling" : "Land"}</p>
                <p className="mt-1 text-pine">
                  {l.units?.length
                    ? `${l.units[0].land_ara} ara (${(l.units[0].land_ara * 100).toLocaleString()} m²)`
                    : `${l.land_sqm.toLocaleString()} m²`}
                </p>
              </div>
              {l.development && (
                <div>
                  <p className="text-sage">Masterplan</p>
                  <p className="mt-1 text-pine">{(l.development.total_sqm / 10000).toFixed(2)} ha</p>
                </div>
              )}
              {l.bedrooms && (
                <div>
                  <p className="text-sage">Bedrooms</p>
                  <p className="mt-1 text-pine">{l.bedrooms}</p>
                </div>
              )}
              {l.yield_pct && (
                <div>
                  <p className="text-sage">Rental yield</p>
                  <p className="mt-1 text-pine">{l.yield_pct}% p.a.</p>
                </div>
              )}
              {l.appreciation_pct && (
                <div>
                  <p className="text-sage">Appreciation</p>
                  <p className="mt-1 text-pine">{l.appreciation_pct}% p.a.</p>
                </div>
              )}
              <div>
                <p className="text-sage">Status</p>
                <p className="mt-1 capitalize text-pine">{l.status}</p>
              </div>
            </div>

            <p className="mt-5 leading-relaxed text-moss">{l.description}</p>

            <ul className="mt-5 space-y-2">
              {l.highlights.map((h) => (
                <li key={h} className="flex items-start gap-2 text-sm text-pine">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-terracotta" />
                  {h}
                </li>
              ))}
            </ul>

            <div className="mt-8 border-t border-pine/10 pt-6">
              {plot && !sent && (
                <div
                  className="mb-4 flex items-center justify-between rounded-xl bg-terracotta/10 px-4 py-3 text-sm text-pine"
                  data-testid="listing-modal-selected-plot"
                >
                  <span>
                    Requesting <strong>Plot {plot.number}</strong> · {plot.sqm.toLocaleString()} m² ·{" "}
                    {plot.price_on_request ? "price on request" : plotPriceLabel(l, plot.sqm, plot.price_idr_per_are || undefined)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPlot(null)}
                    className="font-mono text-[10px] uppercase tracking-[0.16em] text-terracotta hover:underline"
                    data-testid="listing-modal-clear-plot"
                  >
                    Clear
                  </button>
                </div>
              )}
              {sent ? (
                <p className="font-display text-xl" data-testid="listing-modal-sent">
                  Received — we'll send the full dossier.
                </p>
              ) : (
                <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row">
                  <input
                    required
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full border-b border-pine/25 bg-transparent py-2.5 outline-none placeholder:text-sage focus:border-terracotta"
                    data-testid="listing-modal-name-input"
                  />
                  <input
                    required
                    type="email"
                    placeholder="Email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full border-b border-pine/25 bg-transparent py-2.5 outline-none placeholder:text-sage focus:border-terracotta"
                    data-testid="listing-modal-email-input"
                  />
                  <button
                    type="submit"
                    data-testid="listing-modal-submit"
                    className="shrink-0 rounded-full bg-ink px-6 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-cream transition-colors hover:bg-terracotta"
                  >
                    {plot ? `Request Plot ${plot.number}` : "Request details"}
                  </button>
                </form>
              )}
              <a
                href={`${API}/listings/${l.id}/brochure`}
                download
                data-testid="listing-brochure-download"
                className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-pine transition-colors hover:text-terracotta"
              >
                <FileDown className="h-3.5 w-3.5" /> Download PDF brochure
              </a>
              {!l.units?.length && !l.development && (
                <div className="mt-6">
                  <PaymentOptions
                    price={l.price_usd}
                    incomeRate={l.yield_pct || l.appreciation_pct || 0}
                    incomeLabel={l.yield_pct ? "rental income" : "land appreciation"}
                    slug="listing"
                    financing={l.type !== "land"}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {l.videos?.length > 0 && <VideoGallery videos={l.videos} slug={l.id} />}

        {l.development && <DevelopmentSection l={l} plot={plot} onSelectPlot={setPlot} />}

        {l.units?.length > 0 && (
          <div
            className="border-t border-pine/10 px-6 pb-10 pt-8 md:px-10"
            data-testid="listing-villas"
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-pine">
              The villas
            </p>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-moss">
              The resort holds ten villas, each with its own name — but only
              two designs. Within each type the villas are identical, so we
              present one of each.
            </p>
            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              {l.units.map((unit) => {
                const slug = unit.name.toLowerCase().replace(/\s+/g, "-");
                return (
                  <div
                    key={unit.name}
                    data-testid={`villa-card-${slug}`}
                    className="rounded-2xl border border-pine/10 bg-sand/50 p-8 md:p-10"
                  >
                    {unit.images?.length > 0 && (
                      <VillaGallery images={unit.images} name={unit.name} slug={slug} />
                    )}
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <div>
                        <h3 className="font-display text-3xl md:text-4xl">{unit.name}</h3>
                        <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-moss">
                          {unit.levels === 2
                            ? "Two levels"
                            : unit.levels === 1
                            ? "One level"
                            : ""}
                          {unit.count ? ` · ${unit.count} in the resort` : ""}
                        </p>
                      </div>
                      <p className="font-mono text-2xl text-terracotta">
                        {currency(unit.price_usd)}
                      </p>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 font-mono text-[10px] uppercase tracking-[0.14em] text-moss">
                      <span>
                        Land · {unit.land_ara} ara ({(unit.land_ara * 100).toLocaleString()} m²)
                      </span>
                      <span>Villa · {unit.villa_sqm} m²</span>
                      {unit.features.map((f) => (
                        <span key={f}>{f}</span>
                      ))}
                    </div>
                    <div className="mt-4">
                      <VillaRoi unit={unit} />
                      <PaymentOptions
                        price={unit.price_usd}
                        incomeRate={13}
                        incomeLabel="rental income"
                        slug={slug}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

export default function Portfolio() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("all");
  const [price, setPrice] = useState("any");
  const [sort, setSort] = useState("featured");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);

  useEffect(() => {
    fetch(`${API}/listings`)
      .then((r) => r.json())
      .then((d) => setListings(Array.isArray(d) ? d : []))
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  }, []);

  const shown = useMemo(() => {
    const p = PRICES.find((x) => x.id === price);
    return listings
      .filter(
        (l) =>
          (type === "all" || l.type === type) &&
          (p.min === null || l.price_usd >= p.min) &&
          (p.max === null || l.price_usd <= p.max) &&
          (q === "" ||
            [l.title, l.location, l.region]
              .join(" ")
              .toLowerCase()
              .includes(q.toLowerCase()))
      )
      .sort(sorters[sort]);
  }, [listings, type, price, sort, q]);

  return (
    <main className="mx-auto max-w-[1440px] px-6 pb-24 pt-32 md:px-10 md:pt-44">
      <Reveal>
        <Eyebrow>The portfolio</Eyebrow>
        <div className="mt-5 flex flex-wrap items-end justify-between gap-6">
          <h1 className="font-display text-5xl leading-none md:text-7xl">
            Villas & land, <em className="text-terracotta">as they come</em>
          </h1>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-sage" data-testid="portfolio-count">
            {shown.length} of {listings.length} listings
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-12 flex flex-col gap-4 border-y border-pine/10 py-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2" data-testid="portfolio-type-filter">
            {TYPES.map((t) => (
              <button
                key={t.id}
                onClick={() => setType(t.id)}
                data-testid={`filter-type-${t.id}`}
                className={`rounded-full px-5 py-2 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors ${
                  type === t.id
                    ? "bg-ink text-cream"
                    : "border border-pine/20 text-moss hover:border-terracotta hover:text-terracotta"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative">
              <Search className="pointer-events-none absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-sage" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search location…"
                data-testid="portfolio-search-input"
                className="w-full border-b border-pine/20 bg-transparent py-2 pl-7 pr-2 outline-none placeholder:text-sage focus:border-terracotta sm:w-48"
              />
            </div>
            <select
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              data-testid="portfolio-price-select"
              className="border-b border-pine/20 bg-transparent py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-moss outline-none focus:border-terracotta [&>option]:text-pine"
            >
              {PRICES.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="portfolio-sort-select"
              className="border-b border-pine/20 bg-transparent py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-moss outline-none focus:border-terracotta [&>option]:text-pine"
            >
              {SORTS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Reveal>

      <div className="mt-12 grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
        {loading ? (
          <p className="col-span-full py-20 text-center font-mono text-xs uppercase tracking-[0.2em] text-sage">
            Loading the portfolio…
          </p>
        ) : shown.length ? (
          shown.map((l) => <Card key={l.id} l={l} onOpen={setActive} />)
        ) : (
          <p className="col-span-full py-20 text-center font-display text-2xl text-moss" data-testid="portfolio-empty">
            Nothing matches that yet — widen the filters.
          </p>
        )}
      </div>

      {active && <Modal l={active} onClose={() => setActive(null)} />}
    </main>
  );
}
